import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import {
  tap,
  switchMap,
  map,
  filter,
  catchError,
  finalize,
  shareReplay,
} from "rxjs/operators";
import * as Sentry from "@sentry/angular-ivy";
import { NgxRolesService } from "ngx-permissions";
import {
  ALL_PERMISSIONS,
  ANALYST_PERMISSIONS,
  USER_PERMISSIONS,
} from "../permissions/permissions";
import { ToastrService } from "ngx-toastr";
// import mixpanel from 'mixpanel-browser';
import { GoogleAnalyticsService } from "ngx-google-analytics";
import { Apollo } from "apollo-angular";
import {
  Login,
  LoginVariables,
  Login_login,
} from "src/models/phoenix-auth";
import {
  VerifyMfaOtp,
  VerifyMfaOtpVariables,
  VerifyMfaOtp_verifyMfaOtp,
} from "src/models/phoenix-auth";
import {
  ResendOtp,
  ResendOtpVariables,
} from "src/models/phoenix-auth";
import {
  RefreshToken,
  RefreshTokenVariables,
} from "../../models/phoenix-auth";
import {
  ResetPassword,
  ResetPasswordVariables,
} from "../../models/phoenix-auth";
import {
  ForgotPassword,
  ForgotPasswordVariables,
} from "../../models/phoenix-auth";
import {
  FinishInviteUserMutation,
  ForgetPasswordMutation,
  LoginMutation,
  VerifyMfaOtpMutation,
  ResendOtpMutation,
  RefreshTokenMutation,
  ResetPasswordMutation,
} from "../../app/queries/phoenix-auth/auth";
import {
  FinishInviteInput,
  RefreshTokenInput,
  VerifyMfaOtpInput,
  ResendOtpInput,
} from "../../models/phoenix-auth";
import { FinishUserInvite } from "../../models/phoenix-auth";
import { ChangePasswordVariables } from "../../models/phoenix";
import type * as PhoenixTypes from "../../models/phoenix";
import { ChangePassword, MeQuery } from "../../app/queries/phoenix/users";
import { Router } from "@angular/router";
import { Me, Me_me_objects } from "../../models/phoenix";
import type { FinishUserInviteVariables } from "../../models/phoenix-auth";

const NO_REFRESH = true;

const permissionsByRoles = {
  Admin: [...ALL_PERMISSIONS],
  Analyst: [...ANALYST_PERMISSIONS],
  User: [...USER_PERMISSIONS],
};

type SupportedRole = keyof typeof permissionsByRoles;

@Injectable({
  providedIn: "root",
})
export class AuthNewService {
  private authSubject = new BehaviorSubject<VerifyMfaOtp_verifyMfaOtp | null>(null);
  private ensureAuthenticatedRequest$: Observable<boolean> | null = null;
  auth$: Observable<VerifyMfaOtp_verifyMfaOtp | null> = this.authSubject.asObservable();

  url = environment.auth + "/login";

  public get userValue(): VerifyMfaOtp_verifyMfaOtp | null {
    return this.authSubject.value;
  }

  constructor(
    private http: HttpClient,
    private rolesService: NgxRolesService,
    private toastr: ToastrService,
    private apollo: Apollo,
    protected gaService: GoogleAnalyticsService,
    private router: Router
  ) {
    localStorage.removeItem("way2vat");
  }

  get expires$() {
    return this.auth$.pipe(
      map((auth) => auth?.access_token),
      filter((accessToken): accessToken is string => !!accessToken),
      map((accessToken: string) => {
        const jwtToken = JSON.parse(atob(accessToken.split(".")[1]));

        return new Date(jwtToken.exp * 1000);
      })
    );
  }

  refresh = (
    input: RefreshTokenInput = {
      refresh_token: localStorage.getItem("refresh_token"),
    }
  ) => {
    // console.log(
    // `-- refresh was called, time: ${new Date(Date.now())} -- `,
    //   this
    // );

    const variables: RefreshTokenVariables = {
      input,
    };

    return this.apollo
      .use("auth")
      .mutate<RefreshToken>({
        mutation: RefreshTokenMutation,
        variables,
      })
      .pipe(
        map((response) => response.data.refreshToken),
        tap((response) =>
          this.authSubject.next(
            this.userValue
              ? {
                  ...this.userValue,
                  access_token: response.access_token,
                }
              : null
          )
        ),
        tap((refreshToken) =>
          localStorage.setItem("access_token", refreshToken.access_token)
        ),
        tap((response) => {
          this.startRefreshTokenTimer();
        }),
        catchError((error) => {
          this.clearAuthState(false);
          return throwError(error);
        })
      );
  };

  ensureAuthenticated(): Observable<boolean> {
    localStorage.removeItem("way2vat");

    if (this.userValue) {
      return of(true);
    }

    if (this.ensureAuthenticatedRequest$) {
      return this.ensureAuthenticatedRequest$;
    }

    this.ensureAuthenticatedRequest$ = this.restoreAuthenticatedSession().pipe(
      finalize(() => (this.ensureAuthenticatedRequest$ = null)),
      shareReplay(1)
    );

    return this.ensureAuthenticatedRequest$;
  }

  private restoreAuthenticatedSession(): Observable<boolean> {
    const accessToken = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");

    if (!accessToken && !refreshToken) {
      this.clearAuthState(false);
      return of(false);
    }

    const readyToken$ =
      accessToken && !this.isJwtExpired(accessToken)
        ? of(accessToken)
        : refreshToken && !this.isJwtExpired(refreshToken)
        ? this.refresh().pipe(map((response) => response.access_token))
        : of(null);

    return readyToken$.pipe(
      switchMap((token) => {
        if (!token) {
          this.clearAuthState(false);
          return of(false);
        }

        return this.loadCurrentUser();
      }),
      catchError(() => {
        this.clearAuthState(false);
        return of(false);
      })
    );
  }

  signIn(username: string, password: string): Observable<Login_login> {
    console.log(`-- login, time: ${new Date(Date.now())} -- `);

    const variables: LoginVariables = {
      loginInput: {
        email: username,
        password,
      },
    };

    return this.apollo
      .use("auth")
      .mutate<Login>({
        mutation: LoginMutation,
        variables,
      })
      .pipe(
        map((response) => response.data.login),
      );
  }

  verifyOtp(input: VerifyMfaOtpInput): Observable<VerifyMfaOtp_verifyMfaOtp | null> {
    const variables: VerifyMfaOtpVariables = { input };

    return this.apollo
      .use("auth")
      .mutate<VerifyMfaOtp>({
        mutation: VerifyMfaOtpMutation,
        variables,
      })
      .pipe(
        map((response) => response.data.verifyMfaOtp),
        tap((response) => {
          Sentry.setUser({
            email: response.payload.email,
            role: response.payload.role,
            name: `${response.payload.firstName} ${response.payload.lastName}`,
            id: response.payload.userId,
          });
          localStorage.setItem("access_token", response.access_token);
          localStorage.setItem("refresh_token", response.refresh_token);
        }),
        tap(() => localStorage.removeItem("way2vat")),
        tap((response) => this.setAuthState({ ...response })),
        tap(() => NO_REFRESH || this.startRefreshTokenTimer()),
        switchMap(() => this.auth$)
      );
  }

  resendOtp(input: ResendOtpInput) {
    const variables: ResendOtpVariables = { input };

    return this.apollo
      .use("auth")
      .mutate<ResendOtp>({
        mutation: ResendOtpMutation,
        variables,
      })
      .pipe(map((response) => response.data.resendOtp));
  }

  signOut(): void {
    this.clearAuthState(true);
  }

  private clearAuthState(navigate: boolean): void {
    localStorage.removeItem("__way2vat.user");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("way2vat");

    this.apollo.use("extra").client.clearStore();
    this.apollo.use("wv").client.clearStore();
    this.apollo.use("phoenix").client.clearStore();

    this.rolesService.flushRolesAndPermissions();
    this.authSubject.next(null);

    NO_REFRESH || this.stopRefreshTokenTimer();

    if (navigate) {
      this.router.navigate(["/auth/sign-in"]);
    }
  }

  forgotPassword(email) {
    const variables: ForgotPasswordVariables = {
      input: {
        email,
      },
    };

    return this.apollo.use("auth").mutate<ForgotPassword>({
      mutation: ForgetPasswordMutation,
      variables,
    });
  }

  updatePassword(currentPassword, newPassword) {
    const variables: ChangePasswordVariables = {
      input: {
        currentPassword,
        newPassword,
      },
    };

    return this.apollo
      .use("phoenix")
      .mutate<PhoenixTypes.ChangePassword>({
        mutation: ChangePassword,
        variables,
      })
      .pipe(tap(() => this.toastr.success("Password Changed successfully")));
  }

  setNewPassword(newPassword, token) {
    const variables: ResetPasswordVariables = {
      input: {
        newPassword,
        token,
      },
    };

    return this.apollo
      .use("auth")
      .mutate<ResetPassword>({
        mutation: ResetPasswordMutation,
        variables,
      })
      .pipe(
        tap((response) => this.toastr.success("Password changed successfully"))
      );
  }

  finishInvite(input: FinishInviteInput) {
    return this.apollo
      .use("auth")
      .mutate<FinishUserInvite, FinishUserInviteVariables>({
        mutation: FinishInviteUserMutation,
        variables: {
          input,
        },
      })
      .pipe(
        map((response) => response.data.finishUserInvite),
        tap(() => this.toastr.success("User created successfully"))
        // tap((response) => console.log("--2", response))
      );
  }

  private refreshTokenTimeout;

  private loadCurrentUser(): Observable<boolean> {
    return this.apollo
      .use("phoenix")
      .query<Me>({
        query: MeQuery,
        fetchPolicy: "network-only",
      })
      .pipe(
        map((response) => response.data?.me?.objects?.find(Boolean) || null),
        map((user) => {
          if (!user?.role) {
            this.clearAuthState(false);
            return false;
          }

          this.setAuthState(this.toAuthSession(user));
          return true;
        })
      );
  }

  private toAuthSession(user: Me_me_objects): VerifyMfaOtp_verifyMfaOtp {
    // Built locally (not a server response): keeps its previous __typename values,
    // the schema now names these types AuthSession / AuthSessionPayload.
    return {
      __typename: "VerifyMfaOtpResponse" as string as VerifyMfaOtp_verifyMfaOtp["__typename"],
      payload: {
        __typename: "LoginResponsePayload" as string as VerifyMfaOtp_verifyMfaOtp["payload"]["__typename"],
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role as any,
        userId: String(user.id),
        companiesIds: (user.companies || [])
          .filter((company): company is NonNullable<typeof company> => !!company)
          .map((company) => company.id),
      },
      access_token: localStorage.getItem("access_token"),
      refresh_token: localStorage.getItem("refresh_token"),
    };
  }

  private setAuthState(auth: VerifyMfaOtp_verifyMfaOtp): void {
    this.authSubject.next(auth);
    this.applyPermissions(auth.payload.role?.toString());
    Sentry.setUser({
      email: auth.payload.email,
      role: auth.payload.role,
      name: `${auth.payload.firstName || ""} ${auth.payload.lastName || ""}`.trim(),
      id: auth.payload.userId,
    });
  }

  private applyPermissions(role: string | null | undefined): void {
    this.rolesService.flushRolesAndPermissions();

    const roleKey = this.getSupportedRole(role);
    if (!roleKey) {
      return;
    }

    this.rolesService.addRoleWithPermissions(
      roleKey,
      permissionsByRoles[roleKey]
    );
  }

  private getSupportedRole(role: string | null | undefined): SupportedRole | null {
    return (Object.keys(permissionsByRoles) as SupportedRole[]).find(
      (key) => key.toLowerCase() === role?.toLowerCase()
    ) || null;
  }

  private isJwtExpired(token: string): boolean {
    const jwtToken = this.parseJwt(token);
    if (!jwtToken?.exp) {
      return true;
    }

    return Number(jwtToken.exp * 1000) < Date.now();
  }

  private parseJwt(token: string | null): any | null {
    try {
      if (!token) {
        return null;
      }

      return JSON.parse(atob(token.split(".")[1]));
    } catch {
      return null;
    }
  }

  private startRefreshTokenTimer() {
    if (this.refreshTokenTimeout) {
      this.stopRefreshTokenTimer();
    }
    // parse json object from base64 encoded jwt token
    const jwtToken = JSON.parse(
      atob(localStorage.getItem("access_token").split(".")[1])
    );

    // set a timeout to refresh the token a minute before it expires
    const expires = new Date(jwtToken.exp * 1000);

    console.log(`-- expires at, time: ${expires} -- `);

    const timeout = expires.getTime() - Date.now() - 60 * 1000;

    console.log(
      `-- timeout at, time: ${new Date(expires.getTime() - 60 * 1000)} -- `
    );

    this.refreshTokenTimeout = setTimeout(
      () => this.refresh().subscribe(),
      timeout
    );
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
}
